import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheckIcon, AlertCircleIcon, DollarSignIcon, SettingsIcon, ClockIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface PreAuthPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function PreAuthPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: PreAuthPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [captureOption, setCaptureOption] = useState<'manual' | 'auto'>('manual');
  const [autoDays, setAutoDays] = useState('7');
  const [autoDaysOverridden, setAutoDaysOverridden] = useState(false);
  const [validityDays, setValidityDays] = useState(7);
  // Keep automatic-capture day count in sync with the pre-auth validity
  // unless the user has manually overridden it.
  useEffect(() => {
    if (!autoDaysOverridden) {
      setAutoDays(String(validityDays));
    } else {
      const current = parseInt(autoDays, 10);
      if (!Number.isNaN(current) && current > validityDays) {
        setAutoDays(String(validityDays));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityDays, autoDaysOverridden]);
  const handleAmountChange = (v: string) => onAmountChange?.(v);
  const handleReferenceChange = (v: string) => onReferenceChange?.(v);
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-7552">
      {/* ─── PRE-AUTHORIZATION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7553">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7554">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7555">
            <ShieldCheckIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7556" />
            {t('components.PreAuthPayment.preauthorization_details')}
          </h2>
          <p className="text-xs text-gray-500 mt-1" data-id="element-7557">
            {t('components.PreAuthPayment.place_a_temporary_hold_on_the')}
          </p>
        </div>

        <div className="p-6 space-y-4" data-id="element-7558">
          <div data-id="element-7559">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7560">
              {t('components.PreAuthPayment.hold_amount')}{' '}
              <span className="text-red-400" data-id="element-7561">*</span>
            </label>
            <div className="relative" data-id="element-7562">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7563">
                <span className="text-sm font-medium text-gray-500" data-id="element-7564">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" onChange={e => handleAmountChange(e.target.value)} data-id="element-7565" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7566">
            <div data-id="element-7567">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7568">
                {t('components.BulkPaymentPreview.reference')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.PreAuthPayment.attrs.eg_booking_1234')} onChange={e => handleReferenceChange(e.target.value)} data-id="element-7569" />
            </div>
            <div data-id="element-7570">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7571">
                {t('components.BulkPaymentPreview.description')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.PreAuthPayment.attrs.eg_security_deposit')} data-id="element-7572" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── PRE-AUTH VALIDITY ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7573">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7574">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7575">
            <ClockIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7576" />
            Pre-authorization validity
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-7577">
            Choose how long the hold remains valid before it automatically
            expires.
          </p>
        </div>
        <div className="px-6 py-5" data-id="element-7578">
          <div className="flex items-baseline justify-between mb-3" data-id="element-7579">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7580">
              Hold valid for
            </label>
            <div className="flex items-baseline gap-1.5" data-id="element-7581">
              <span className="text-2xl font-semibold text-gray-900 tabular-nums" data-id="element-7582">
                {validityDays}
              </span>
              <span className="text-sm text-gray-500" data-id="element-7583">days</span>
            </div>
          </div>
          <input type="range" min={7} max={21} step={1} value={validityDays} onChange={e => setValidityDays(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#E2231A]" data-id="element-7584" />
          <div className="flex justify-between text-xs text-gray-400 mt-2" data-id="element-7585">
            <span data-id="element-7586">7 days</span>
            <span data-id="element-7587">14 days</span>
            <span data-id="element-7588">21 days</span>
          </div>
          <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-7589">
            <AlertCircleIcon size={14} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-7590" />
            <p className="text-xs text-amber-800" data-id="element-7591">
              If not captured within {validityDays} day
              {validityDays === 1 ? '' : 's'}, the hold will be released and
              funds returned to the cardholder.
            </p>
          </div>
        </div>
      </div>

      {/* ─── CAPTURE OPTIONS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7592">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7593">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7594">
            <SettingsIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7595" />
            {t('components.PreAuthPayment.capture_options')}
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-7596">
            {t('components.PreAuthPayment.choose_how_and_when_to_charge')}
          </p>
        </div>

        <div className={`flex items-center px-6 py-4 cursor-pointer transition-colors border-b border-gray-200 ${captureOption === 'manual' ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('manual')} data-id="element-7597">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={captureOption === 'manual'} onChange={() => setCaptureOption('manual')} data-id="element-7598" />

          <div className="ml-4" data-id="element-7599">
            <p className="text-sm font-medium text-gray-900" data-id="element-7600">
              {t('components.PreAuthPayment.manual_capture')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-7601">
              {t('components.PreAuthPayment.charge_funds_manually_when_needed_from')}
            </p>
          </div>
        </div>

        <div className={`flex items-start px-6 py-4 cursor-pointer transition-colors ${captureOption === 'auto' ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('auto')} data-id="element-7602">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={captureOption === 'auto'} onChange={() => setCaptureOption('auto')} data-id="element-7603" />

          <div className="ml-4 flex-1" data-id="element-7604">
            <p className="text-sm font-medium text-gray-900" data-id="element-7605">
              {t('components.PreAuthPayment.automatic_capture')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-7606">
              {t('components.PreAuthPayment.automatically_charge_the_held_amount_after')}
            </p>
            {captureOption === 'auto' && <div className="mt-3 flex items-center gap-2" data-id="element-7607">
                <label className="text-xs text-gray-500" data-id="element-7608">
                  {t('components.PreAuthPayment.capture_after')}
                </label>
                <input type="number" className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" value={autoDays} onChange={e => {
              setAutoDays(e.target.value);
              setAutoDaysOverridden(true);
            }} min="1" max={validityDays} onClick={e => e.stopPropagation()} data-id="element-7609" />

                <span className="text-xs text-gray-500" data-id="element-7610">
                  {t('components.PreAuthPayment.days')}
                </span>
                {autoDaysOverridden && <button type="button" onClick={e => {
              e.stopPropagation();
              setAutoDaysOverridden(false);
              setAutoDays(String(validityDays));
            }} className="ml-1 text-[11px] text-[#9F140F] hover:underline" data-id="element-7611">
                    Reset to validity ({validityDays}d)
                  </button>}
                {!autoDaysOverridden && <span className="ml-1 text-[11px] text-gray-400" data-id="element-7612">
                    Matches validity window
                  </span>}
              </div>}
          </div>
        </div>
      </div>

      {/* ─── ADDITIONAL OPTIONS ─── */}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-7613" />
    </div>;
}